import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import type { Vehicle } from '@msc-stock/shared';
import { api, fetchGroups, type Facets, type GroupStat } from '../lib/api.js';
import { formatDate, useI18n, yen } from '../i18n.js';
import { Badge, Bars, Card, DivisionBadge, Empty, Field, Notice, Tabs } from '../components/ui.js';

type Mode = 'in_stock' | 'sold';

/** グループ分けできる軸。利用者が言う「メーカー・車名・オークション」に対応する。 */
const GROUP_OPTIONS = ['none', 'maker', 'model', 'supplier', 'color', 'year'] as const;
type GroupBy = (typeof GROUP_OPTIONS)[number];

const PAGE_SIZE = 50;

/**
 * 在庫 / 売却済みの一覧。
 *
 * 絞り込み条件はすべて URL のクエリに持たせている。こうしておくと、
 * 「USS東京で仕入れた水没の CX-5」のような条件をそのままブックマークしたり、
 * 人に URL で渡したりできる。
 */
export function VehicleListPage({ mode }: { mode: Mode }) {
  const { t, lang } = useI18n();
  const [params, setParams] = useSearchParams();

  const [data, setData] = useState<{
    vehicles: Vehicle[];
    total: number;
    purchaseTotal: number;
    saleTotal: number;
    pricedCount: number;
  } | null>(null);
  const [facets, setFacets] = useState<Facets | null>(null);
  const [groups, setGroups] = useState<GroupStat[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(true);

  const groupBy = (params.get('groupBy') ?? 'none') as GroupBy;
  const page = Math.max(Number(params.get('page') ?? 1) || 1, 1);

  /** API に投げるクエリ。画面の mode を status として必ず付ける。 */
  const query = useMemo(() => {
    const p = new URLSearchParams(params);
    p.delete('groupBy');
    p.set('status', mode);
    p.set('pageSize', String(PAGE_SIZE));
    if (!p.get('sort')) p.set('sort', mode === 'sold' ? 'soldDate' : 'purchaseDate');
    return p;
  }, [params, mode]);

  useEffect(() => {
    let cancelled = false;
    setBusy(true);
    setError(null);
    api
      .vehicles(query)
      .then((r) => {
        if (!cancelled) setData(r);
      })
      .catch((e) => !cancelled && setError(e instanceof Error ? e.message : String(e)))
      .finally(() => !cancelled && setBusy(false));
    return () => {
      cancelled = true;
    };
  }, [query]);

  useEffect(() => {
    api.facets().then(setFacets).catch(() => setFacets(null));
  }, []);

  useEffect(() => {
    if (groupBy === 'none') {
      setGroups(null);
      return;
    }
    let cancelled = false;
    fetchGroups(query, groupBy)
      .then((r) => !cancelled && setGroups(r.groups))
      .catch(() => !cancelled && setGroups(null));
    return () => {
      cancelled = true;
    };
  }, [query, groupBy]);

  /** 条件を 1 つ変える。ページ番号は 1 に戻す。 */
  const setParam = useCallback(
    (key: string, value: string | null) => {
      const next = new URLSearchParams(params);
      if (value == null || value === '') next.delete(key);
      else next.set(key, value);
      if (key !== 'page') next.delete('page');
      setParams(next, { replace: true });
    },
    [params, setParams],
  );

  const sort = params.get('sort') ?? (mode === 'sold' ? 'soldDate' : 'purchaseDate');
  const order = params.get('order') === 'asc' ? 'asc' : 'desc';

  const toggleSort = (column: string) => {
    const next = new URLSearchParams(params);
    if (sort === column) {
      next.set('order', order === 'asc' ? 'desc' : 'asc');
    } else {
      next.set('sort', column);
      next.set('order', 'desc');
    }
    next.delete('page');
    setParams(next, { replace: true });
  };

  const sortArrow = (column: string) => (sort === column ? (order === 'asc' ? ' ▲' : ' ▼') : '');

  const totalPages = data ? Math.max(1, Math.ceil(data.total / PAGE_SIZE)) : 1;
  const division = params.get('division') ?? '';

  const profit = data && data.pricedCount > 0 ? data.saleTotal : null;

  return (
    <>
      <h1 className="page-title">{mode === 'sold' ? t('sold') : t('stock')}</h1>
      <p className="page-sub">
        {data
          ? `${data.total.toLocaleString('en-US')} ${t('vehicles')} · ${t('purchasePrice')} ${yen(data.purchaseTotal)}`
          : t('loading')}
        {mode === 'sold' && data && data.pricedCount > 0 && (
          <>
            {' · '}
            {t('revenue')} {yen(profit)} ({data.pricedCount})
          </>
        )}
      </p>

      {error && <Notice kind="bad">{error}</Notice>}

      {/* 帳簿の切り替え。HIT と MSC を混ぜずに見られるようにする。 */}
      <div className="row" style={{ marginBottom: 12 }}>
        <Tabs
          value={division}
          onChange={(v) => setParam('division', v)}
          options={[
            { value: '', label: t('all') },
            { value: 'HIT', label: 'HIT' },
            { value: 'MSC', label: 'MSC' },
          ]}
        />
        <div className="spacer" />
        <Tabs
          value={groupBy}
          onChange={(v) => setParam('groupBy', v === 'none' ? null : v)}
          options={[
            { value: 'none', label: t('none') },
            { value: 'maker', label: t('maker') },
            { value: 'model', label: t('model') },
            { value: 'supplier', label: t('supplier') },
          ]}
        />
      </div>

      <Card title={t('filters')} actions={
        <button type="button" className="btn small" onClick={() => setParams(new URLSearchParams(), { replace: true })}>
          {t('clear')}
        </button>
      }>
        <div style={{ marginBottom: 10 }}>
          <input
            type="search"
            placeholder={t('search')}
            defaultValue={params.get('q') ?? ''}
            onChange={(e) => {
              const value = e.target.value;
              // 打ち終わるのを少し待ってから検索する
              window.clearTimeout((window as unknown as { __q?: number }).__q);
              (window as unknown as { __q?: number }).__q = window.setTimeout(
                () => setParam('q', value),
                250,
              );
            }}
          />
        </div>

        <div className="filters">
          <Field label={t('maker')}>
            <select value={params.get('maker') ?? ''} onChange={(e) => setParam('maker', e.target.value)}>
              <option value="">{t('all')}</option>
              {facets?.makers.map((f) => (
                <option key={f.value} value={f.value}>
                  {f.value} ({f.count})
                </option>
              ))}
            </select>
          </Field>

          <Field label={t('model')}>
            <select value={params.get('model') ?? ''} onChange={(e) => setParam('model', e.target.value)}>
              <option value="">{t('all')}</option>
              {facets?.models.map((f) => (
                <option key={f.value} value={f.value}>
                  {f.value} ({f.count})
                </option>
              ))}
            </select>
          </Field>

          <Field label={t('supplier')}>
            <select value={params.get('supplier') ?? ''} onChange={(e) => setParam('supplier', e.target.value)}>
              <option value="">{t('all')}</option>
              {facets?.suppliers.map((f) => (
                <option key={f.value} value={f.value}>
                  {f.value} ({f.count})
                </option>
              ))}
            </select>
          </Field>

          <Field label={t('color')}>
            <select value={params.get('color') ?? ''} onChange={(e) => setParam('color', e.target.value)}>
              <option value="">{t('all')}</option>
              {facets?.colors.map((f) => (
                <option key={f.value} value={f.value}>
                  {f.value} ({f.count})
                </option>
              ))}
            </select>
          </Field>

          <Field label={t('condition')}>
            <select value={params.get('condition') ?? ''} onChange={(e) => setParam('condition', e.target.value)}>
              <option value="">{t('all')}</option>
              <option value="flood">{t('flood')}</option>
              <option value="normal">{t('normal')}</option>
            </select>
          </Field>

          <Field label={t('parts')}>
            <select value={params.get('parts') ?? ''} onChange={(e) => setParam('parts', e.target.value)}>
              <option value="">{t('all')}</option>
              <option value="1">○</option>
              <option value="0">—</option>
            </select>
          </Field>

          <Field label={`${t('purchaseDate')} ⇢`}>
            <input
              type="date"
              value={params.get('purchasedFrom') ?? ''}
              onChange={(e) => setParam('purchasedFrom', e.target.value)}
            />
          </Field>
          <Field label={`⇠ ${t('purchaseDate')}`}>
            <input
              type="date"
              value={params.get('purchasedTo') ?? ''}
              onChange={(e) => setParam('purchasedTo', e.target.value)}
            />
          </Field>

          {mode === 'sold' && (
            <>
              <Field label={`${t('soldDate')} ⇢`}>
                <input
                  type="date"
                  value={params.get('soldFrom') ?? ''}
                  onChange={(e) => setParam('soldFrom', e.target.value)}
                />
              </Field>
              <Field label={`⇠ ${t('soldDate')}`}>
                <input
                  type="date"
                  value={params.get('soldTo') ?? ''}
                  onChange={(e) => setParam('soldTo', e.target.value)}
                />
              </Field>
            </>
          )}

          <Field label={`${t('purchasePrice')} ≧`}>
            <input
              type="number"
              inputMode="numeric"
              value={params.get('priceMin') ?? ''}
              onChange={(e) => setParam('priceMin', e.target.value)}
            />
          </Field>
          <Field label={`${t('purchasePrice')} ≦`}>
            <input
              type="number"
              inputMode="numeric"
              value={params.get('priceMax') ?? ''}
              onChange={(e) => setParam('priceMax', e.target.value)}
            />
          </Field>
        </div>

        {mode === 'sold' && (
          <label className="row" style={{ marginTop: 10, gap: 6 }}>
            <input
              type="checkbox"
              style={{ width: 'auto' }}
              checked={params.get('saleMissing') === '1'}
              onChange={(e) => setParam('saleMissing', e.target.checked ? '1' : null)}
            />
            <span className="faint">{t('saleMissingOnly')}</span>
          </label>
        )}
      </Card>

      {groups && groups.length > 0 && (
        <div style={{ marginTop: 14 }}>
          <Card title={`${t('groupBy')}: ${t(groupBy as never)}`}>
            <Bars
              limit={20}
              data={groups.map((g) => ({
                name: g.key || '—',
                value: g.count,
                display: `${g.count} · ${yen(g.purchaseTotal)}`,
              }))}
            />
          </Card>
        </div>
      )}

      <div style={{ marginTop: 14 }} className="table-wrap">
        {busy && !data ? (
          <Empty>{t('loading')}</Empty>
        ) : !data || data.vehicles.length === 0 ? (
          <Empty>{t('noResults')}</Empty>
        ) : (
          <table>
            <thead>
              <tr>
                <th>{t('division')}</th>
                <th className="sortable" onClick={() => toggleSort('maker')}>
                  {t('maker')}
                  {sortArrow('maker')}
                </th>
                <th className="sortable" onClick={() => toggleSort('model')}>
                  {t('model')}
                  {sortArrow('model')}
                </th>
                <th>{t('grade')}</th>
                <th className="sortable" onClick={() => toggleSort('chassis')}>
                  {t('chassis')}
                  {sortArrow('chassis')}
                </th>
                <th>{t('color')}</th>
                <th className="sortable" onClick={() => toggleSort('year')}>
                  {t('year')}
                  {sortArrow('year')}
                </th>
                <th className="sortable" onClick={() => toggleSort('supplier')}>
                  {t('supplier')}
                  {sortArrow('supplier')}
                </th>
                <th className="sortable" onClick={() => toggleSort('purchaseDate')}>
                  {t('purchaseDate')}
                  {sortArrow('purchaseDate')}
                </th>
                <th className="num sortable" onClick={() => toggleSort('purchasePrice')}>
                  {t('purchasePrice')}
                  {sortArrow('purchasePrice')}
                </th>
                {mode === 'sold' && (
                  <>
                    <th className="sortable" onClick={() => toggleSort('soldDate')}>
                      {t('soldDate')}
                      {sortArrow('soldDate')}
                    </th>
                    <th className="num sortable" onClick={() => toggleSort('salePrice')}>
                      {t('salePrice')}
                      {sortArrow('salePrice')}
                    </th>
                    <th className="num">{t('profit')}</th>
                  </>
                )}
                <th>{t('condition')}</th>
              </tr>
            </thead>
            <tbody>
              {data.vehicles.map((v) => (
                <tr key={v.id}>
                  <td>
                    <DivisionBadge division={v.division} />
                  </td>
                  <td>{v.maker}</td>
                  <td>
                    <Link to={`/vehicles/${v.id}`}>{v.model || '—'}</Link>
                  </td>
                  <td className="wrap muted">{v.grade || '—'}</td>
                  <td className="mono">
                    {v.chassisMissing ? <span className="faint">—</span> : v.chassisRaw}
                  </td>
                  <td className="muted">{v.color || '—'}</td>
                  <td className="muted">{v.yearRaw || '—'}</td>
                  <td>{v.supplier || '—'}</td>
                  <td className="muted">{formatDate(v.purchaseDate, lang)}</td>
                  <td className="num">{yen(v.purchasePrice)}</td>
                  {mode === 'sold' && (
                    <>
                      <td className="muted">{formatDate(v.saleDate ?? v.soldDate, lang)}</td>
                      <td className="num">
                        {v.salePrice == null ? <span className="faint">{t('notEntered')}</span> : yen(v.salePrice)}
                      </td>
                      <td className="num">
                        {v.salePrice == null ? (
                          <span className="faint">—</span>
                        ) : (
                          <span style={{ color: v.salePrice - v.purchasePrice >= 0 ? 'var(--ok)' : 'var(--bad)' }}>
                            {yen(v.salePrice - v.purchasePrice)}
                          </span>
                        )}
                      </td>
                    </>
                  )}
                  <td>
                    {v.condition === 'flood' ? (
                      <Badge kind="warn">{t('flood')}</Badge>
                    ) : (
                      <span className="faint">—</span>
                    )}
                    {v.partsFlag && (
                      <>
                        {' '}
                        <Badge kind="neutral">{t('parts')}</Badge>
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {data && totalPages > 1 && (
        <div className="row" style={{ marginTop: 12, justifyContent: 'center' }}>
          <button
            type="button"
            className="btn small"
            disabled={page <= 1}
            onClick={() => setParam('page', String(page - 1))}
          >
            {t('prev')}
          </button>
          <span className="faint">
            {t('page')} {page} / {totalPages}
          </span>
          <button
            type="button"
            className="btn small"
            disabled={page >= totalPages}
            onClick={() => setParam('page', String(page + 1))}
          >
            {t('next')}
          </button>
        </div>
      )}
    </>
  );
}
